import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { retireNote } from "../data/item-publish-state";

/**
 * What the words on the Keyword Status tab actually mean.
 *
 * This surface distinguishes four things a reader will otherwise conflate:
 * a decision the machine recorded but never sent, one that is genuinely live on
 * Amazon, one that has been undone, and one that was never acted on at all.
 * Confusing "ledger only" with "live on Amazon" is the difference between
 * thinking ads changed and knowing they did.
 *
 * Meteoric let a reader dismiss this permanently into `localStorage`. That is
 * not carried across: the tab is used rarely enough that a reader who dismissed
 * it months ago meets the same nine terms with no way back. Collapsing costs a
 * click; dismissing-forever costs the vocabulary.
 *
 * It opens by DEFAULT, as Meteoric's did. A collapsed accordion renders none
 * of its content into the DOM, so a closed-by-default glossary is invisible to
 * a find-in-page and to a screen reader scanning the tab — on the one panel
 * whose entire purpose is that the reader does not yet know these words.
 */
const TERMS: ReadonlyArray<readonly [string, string]> = [
  ["Depth", "How many tactics are stacked on this keyword right now. 0 means the machine has done nothing to it."],
  ["No tactic applied", "The machine has not acted on this keyword."],
  ["Nothing in force", "The machine acted here, and every application has since been retired. Nothing is in effect on Amazon now."],
  ["Ledger only", "The machine decided this, but nothing has reached Amazon. Ads are unchanged."],
  ["Live on Amazon", "This is really in effect on Amazon right now."],
  ["Reversed", "This was live on Amazon and has been undone."],
  ["Publish", "Send this decision to Amazon for real. This changes live ads."],
  ["Retire ledger entry", `Cancel a decision that never reached Amazon. ${retireNote()}`],
  ["Reverse", "Undo something that IS live on Amazon. Ads can serve on this query again."],
];

export function StatusLegend() {
  return (
    <Accordion type="single" collapsible defaultValue="legend">
      <AccordionItem value="legend">
        <AccordionTrigger>What these words mean</AccordionTrigger>
        <AccordionContent>
          <dl className="grid gap-x-6 gap-y-3 text-xs md:grid-cols-2">
            {TERMS.map(([term, copy]) => (
              <div key={term} className="grid grid-cols-[8.5rem_1fr] gap-2">
                <dt className="font-semibold text-ks-ink">{term}</dt>
                <dd className="text-muted-foreground">{copy}</dd>
              </div>
            ))}
          </dl>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
